import * as React from "react";
import { BookOfMindQuote } from "motw_core/DTOs/BookOfMindQuote";
import { IsMobileDevice } from "source/app/domain/Device/Helpers/IsMobile";
import { StyleClass } from "source/app/presentation/Shared/Styles/CSS";
import IconWithText from "source/app/services/Shared/IconWithText/IconWithText";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import { BookOfMind } from "source/app/presentation/MainPanel/Workflows/BookOfMind/BookOfMind";
import { SetBookOfMindState } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Helpers/BookOfMindHelpers";
import { bookOfMindSpeech } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Speech/BookOfMindSpeech";
import MotwLogicServer from "source/app/services/External/LogicServer/MotwLogicServer";
import * as toastr from "toastr";

const RELATED_QUOTES_PAGE_SIZE = 20;

export function JumpToParent({
    selectedQuote,
    quoteParentName,
    parentCollectionName,
}: {
    selectedQuote: BookOfMindQuote | undefined;
    quoteParentName: string;
    parentCollectionName?: string;
}) {
    if (!selectedQuote) {
        return null;
    }

    const jumpToParentStyleClass = StyleClass(`jumpToBookOfMindParent_header`, {
        padding: `3px`,
        background: `radial-gradient(ellipse at top, rgba(143, 107, 55, 0.1), transparent 72%), repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.018) 0 1px, transparent 1px 4px), rgba(33, 33, 33, 0.68)`,
        borderRadius: `4px`,
        zIndex: 1,
        border: `1px solid rgba(143, 107, 55, 0.28)`,
        display: `flex`,
        position: `relative`,
        width: `calc(100% - 30px)`,
        maxWidth: `calc(100% - 30px)`,
        margin: `0 15px`,
        boxSizing: `border-box`,
        flexDirection: `column`,
        gap: `0.35rem`,
    });

    const actionsStyleClass = StyleClass(`jumpToBomParentActions`, {
        display: `flex`,
        gap: `0.35rem`,
        width: `100%`,
    });

    const iconWithTextStyleClass = StyleClass(
        `jumpToBomParentLink_${IsMobileDevice() ? "touch" : "desktop"}`,
        {
        display: `inline-flex`,
        alignItems: `center`,
        minWidth: 0,
        width: `100%`,
        minHeight: `25px`,
        padding: `6px 14px`,
        borderRadius: `3px`,
        background: `rgba(255, 255, 255, 0.035)`,
        border: `1px solid rgba(143, 107, 55, 0.16)`,
        boxSizing: `border-box`,
        opacity: 0.82,
        transition: `background 120ms ease, border-color 120ms ease, color 120ms ease, transform 120ms ease`,
        whiteSpace: `nowrap`,
        overflow: `hidden`,
        ...(IsMobileDevice()
            ? {}
            : {
                  ":hover": {
                      background: `rgba(173, 139, 62, 0.18)`,
                      borderColor: `rgba(173, 139, 62, 0.34)`,
                      color: `rgba(245, 226, 164, 0.98)`,
                  },
              }),
        ":active": {
            background: `rgba(173, 139, 62, 0.18)`,
            borderColor: `rgba(173, 139, 62, 0.34)`,
            color: `rgba(245, 226, 164, 0.98)`,
        },
        }
    );

    const actionContentStyleClass = StyleClass(`jumpToBomParentActionContent`, {
        display: `inline-grid`,
        gridTemplateColumns: `1.1em auto`,
        columnGap: `0.4em`,
        alignItems: `center`,
        lineHeight: 1.2,
        "& > span:first-child": {
            top: 0,
            width: `1.1em`,
            textAlign: `center`,
            lineHeight: 1,
        },
        "& > span:last-child": {
            marginLeft: 0,
            lineHeight: 1.2,
        },
    });

    const actionLinkStyleClass = StyleClass(`jumpToBomParentActionLink`, {
        position: `absolute`,
        inset: 0,
        display: `flex`,
        alignItems: `center`,
        justifyContent: `center`,
        width: `100%`,
        height: `100%`,
        textDecoration: `none`,
    });

    const actionStyle = {
        flex: `1 1 0`,
        alignSelf: `stretch` as const,
        position: `relative` as const,
        boxSizing: `border-box` as const,
        minWidth: 0,
        minHeight: `25px`,
        justifyContent: `center`,
        textAlign: `center` as const,
    };
    const parentActionStyle = {
        ...actionStyle,
        opacity: parentCollectionName ? 1 : 0.42,
        pointerEvents: parentCollectionName ? (`auto` as const) : (`none` as const),
    };

    return (
        <div className={jumpToParentStyleClass}>
            <div className={actionsStyleClass}>
                <IconWithText
                    iconSuffix={EGlyphicon["eye-open"]}
                    whenClicked={() =>
                        void LoadRelatedQuotes(selectedQuote)
                    }
                    displayAsBlock={false}
                    fadeInText={false}
                    withClass={iconWithTextStyleClass}
                    linkClass={actionLinkStyleClass}
                    innerStyleClass={actionContentStyleClass}
                    customStyle={actionStyle}
                    useButton={true}
                >
                    related
                </IconWithText>
                <IconWithText
                    iconSuffix={EGlyphicon["arrow-right"]}
                    whenClicked={() =>
                        quoteParentName && OpenBookOfMind(quoteParentName)
                    }
                    displayAsBlock={false}
                    fadeInText={false}
                    withClass={iconWithTextStyleClass}
                    linkClass={actionLinkStyleClass}
                    innerStyleClass={actionContentStyleClass}
                    customStyle={actionStyle}
                    useButton={true}
                >
                    author
                </IconWithText>
                <IconWithText
                    iconSuffix={EGlyphicon["arrow-up"]}
                    whenClicked={() =>
                        parentCollectionName &&
                        OpenBookOfMind(parentCollectionName)
                    }
                    displayAsBlock={false}
                    fadeInText={false}
                    withClass={iconWithTextStyleClass}
                    linkClass={actionLinkStyleClass}
                    innerStyleClass={actionContentStyleClass}
                    customStyle={parentActionStyle}
                    useButton={true}
                >
                    parent
                </IconWithText>
                <IconWithText
                    iconSuffix={EGlyphicon.copy}
                    whenClicked={() =>
                        void CopyQuoteToClipboard(selectedQuote, quoteParentName)
                    }
                    displayAsBlock={false}
                    fadeInText={false}
                    withClass={iconWithTextStyleClass}
                    linkClass={actionLinkStyleClass}
                    innerStyleClass={actionContentStyleClass}
                    customStyle={actionStyle}
                    useButton={true}
                >
                    copy
                </IconWithText>
            </div>
        </div>
    );
}

async function LoadRelatedQuotes(selectedQuote: BookOfMindQuote) {
    bookOfMindSpeech.StopSpeech();
    SetBookOfMindState({ isLoadingRelatedQuotes: true });

    try {
        const response = await MotwLogicServer.GetRelatedBookOfMindQuotes(
            selectedQuote.id,
            RELATED_QUOTES_LIMIT
        );

        // Log which matching method the server used (semantic embeddings vs
        // lexical BM25) so we can observe fallback behavior.
        console.log(
            `[bookofmind] related quotes for id=${selectedQuote.id} used method=${response.method} (${response.related.length} results)`
        );

        if (response.related.length === 0) {
            SetBookOfMindState({
                selectedQuote: undefined,
                isLoadingRelatedQuotes: false,
            });
            toastr.info("No related quotes found");
            return;
        }

        const relatedQuotes = response.related.map((entry) => entry.quote);

        SetBookOfMindState({
            selectedQuote: undefined,
            isLoadingRelatedQuotes: false,
            rootNodeName: "Related Quotes",
            remainingQuotesToDisplay: relatedQuotes,
            totalQuotesToDisplay: relatedQuotes.length,
            numberOfQuotesToDisplay: RELATED_QUOTES_PAGE_SIZE,
            searchText: "",
        });
    } catch {
        SetBookOfMindState({
            selectedQuote: undefined,
            isLoadingRelatedQuotes: false,
        });
        toastr.error("Unable to load related quotes");
    }
}

function OpenBookOfMind(nodeName: string) {
    SetBookOfMindState({ selectedQuote: undefined });
    BookOfMind(nodeName);
}

async function CopyQuoteToClipboard(
    selectedQuote: BookOfMindQuote,
    quoteParentName: string
) {
    if (!quoteParentName) {
        toastr.error("Author information is still loading");
        return;
    }

    const text = `${selectedQuote.quote} - ${quoteParentName}`;

    try {
        await navigator.clipboard.writeText(text);
        toastr.success("Quote copied");
        return;
    } catch {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        const didCopy = document.execCommand("copy");
        textArea.remove();

        if (didCopy) {
            toastr.success("Quote copied");
        } else {
            toastr.error("Unable to copy quote");
        }
    }
}

// Related quotes may span at most two regular 20-quote pages.
const RELATED_QUOTES_LIMIT = 40;
